import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { sanitizeNext } from "@bw/shared/sanitize-next";

/**
 * useAuthedRedirect — bounce an ALREADY-authenticated visitor away from a public
 * auth page (/sign-in, /sign-up, /forgot-password, …) to where they were headed.
 *
 * Mirrors the player app's `useAuthRedirect` MECHANISM (returnUrl resolution +
 * open-redirect safety) but uses business-v2's `?next=` convention and
 * `sanitizeNext` (the same helper middleware.ts paired with), defaulting to
 * `/workspaces` for every unified account. This
 * replaces the per-form "bounce already-authenticated users"
 * effects that business-v2 hand-rolled in every auth form; the session is owned
 * entirely by @bw/shared + the BFF, so this hook only decides *where* an authed
 * user lands — zero auth logic.
 *
 * @param defaultNext where to send an authed user when no safe `?next=` is
 *   present (defaults to `/workspaces`). Every requested or default URL is
 *   still subject to same-origin URL sanitization.
 */
export function useAuthedRedirect(defaultNext = "/workspaces"): void {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasRedirected = useRef(false);

  // Capture the requested next from the FIRST render so a later re-render (the
  // navigate itself changing the URL) cannot lose it.
  const requestedNext = useRef<string | null>(null);
  if (requestedNext.current === null) {
    requestedNext.current = searchParams.get("next");
  }

  useEffect(() => {
    if (loading || !user || hasRedirected.current) return;
    hasRedirected.current = true;

    // sanitizeNext rejects open-redirect payloads (//evil.com, protocol-relative,
    // backslash, traversal, /api/, /_next/, and auth pages) → returns null, in
    // which case we fall back to the (also sanitized) default.
    const next = sanitizeNext(requestedNext.current);
    const target = next ?? sanitizeNext(defaultNext) ?? "/workspaces";

    navigate(target, { replace: true });
  }, [user, loading, navigate, defaultNext]);
}
